const OMITTED = 'Error details omitted';
const MAX_MESSAGE_LENGTH = 300;

function scrubUrl(url) {
  if (typeof url !== 'string') return url;
  const queryIndex = url.indexOf('?');
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
}

function scrubMessage(message) {
  if (typeof message !== 'string' || !message) return message;
  const line = message.split(/\r?\n/, 1)[0].trim();
  const jsonIndex = line.search(/[{[]/);
  const cleaned = (jsonIndex === -1 ? line : line.slice(0, jsonIndex)).trim();
  if (!cleaned) return OMITTED;
  if (cleaned.length > MAX_MESSAGE_LENGTH) return OMITTED;
  return cleaned;
}

export function scrubSentryBreadcrumb(breadcrumb) {
  if (!breadcrumb || breadcrumb.category === 'console') return null;
  const next = { ...breadcrumb };
  if (typeof next.message === 'string') next.message = scrubMessage(next.message);
  if (next.data && typeof next.data === 'object') {
    const data = {};
    if (next.data.method) data.method = next.data.method;
    if (next.data.status_code != null) data.status_code = next.data.status_code;
    if (next.data.url) data.url = scrubUrl(String(next.data.url));
    next.data = data;
  }
  return next;
}

function scrubBreadcrumbs(breadcrumbs) {
  if (Array.isArray(breadcrumbs)) {
    return breadcrumbs.map(scrubSentryBreadcrumb).filter(Boolean);
  }
  if (Array.isArray(breadcrumbs?.values)) {
    return {
      ...breadcrumbs,
      values: breadcrumbs.values.map(scrubSentryBreadcrumb).filter(Boolean),
    };
  }
  return breadcrumbs;
}

function scrubSpan(span) {
  if (!span || typeof span !== 'object') return span;
  const data = {};
  const method = span.data?.['http.method'] || span.data?.method;
  const statusCode = span.data?.['http.response.status_code'] ?? span.data?.status_code;
  if (method) data['http.method'] = method;
  if (statusCode != null) data.status_code = statusCode;
  return {
    ...span,
    description: typeof span.description === 'string' ? scrubUrl(span.description) : span.description,
    data,
  };
}

export function scrubSentryEvent(event) {
  if (!event) return event;

  if (event.request) {
    event.request = {
      method: event.request.method,
      url: scrubUrl(event.request.url || ''),
    };
  }

  delete event.user;
  delete event.extra;
  delete event.sdkProcessingMetadata;

  if (event.contexts) {
    const contexts = {};
    for (const key of ['runtime', 'os', 'device', 'app', 'browser']) {
      if (event.contexts[key]) contexts[key] = event.contexts[key];
    }
    if (event.contexts.trace) {
      const { trace_id, span_id, op, status } = event.contexts.trace;
      contexts.trace = { trace_id, span_id, op, status };
    }
    event.contexts = contexts;
  }

  if (event.breadcrumbs) event.breadcrumbs = scrubBreadcrumbs(event.breadcrumbs);
  if (Array.isArray(event.spans)) event.spans = event.spans.map(scrubSpan);

  if (typeof event.message === 'string') {
    event.message = scrubMessage(event.message);
  } else if (event.message && typeof event.message === 'object') {
    event.message = scrubMessage(event.message.formatted || '');
  }

  if (event.exception?.values) {
    for (const value of event.exception.values) {
      if (typeof value.value === 'string') value.value = scrubMessage(value.value);
      if (value.stacktrace?.frames) {
        for (const frame of value.stacktrace.frames) delete frame.vars;
      }
    }
  }

  return event;
}

export const sentryReplayPrivacy = {
  maskAllText: true,
  maskAllInputs: true,
  blockAllMedia: true,
  networkCaptureBodies: false,
  networkDetailAllowUrls: [],
};

export function sentryClientOptions({ dsn, integrations } = {}) {
  return {
    dsn,
    tracesSampleRate: 1.0,
    sendDefaultPii: false,
    includeLocalVariables: false,
    integrations(defaults) {
      const filtered = defaults.filter(
        (integration) => integration.name !== 'Console' && integration.name !== 'CaptureConsole'
      );
      return integrations ? [...filtered, ...integrations] : filtered;
    },
    beforeBreadcrumb(breadcrumb) {
      return scrubSentryBreadcrumb(breadcrumb);
    },
    beforeSend(event) {
      return scrubSentryEvent(event);
    },
    beforeSendTransaction(event) {
      return scrubSentryEvent(event);
    },
  };
}
